import { ConnectionModel } from '@mm/state/model/ConnectionModel';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import { IntegrationRevenue } from '../IntegrationRevenue/IntegrationRevenue';
import { OauthTokenConnection } from '../OauthTokenConnection/OauthTokenConnection';
import { KlaviyoApiKeyConnection } from './KlaviyoApiKeyConnection';
import { KlaviyoConnectionModeSelector } from './KlaviyoConnectionModeSelector';
import { KlaviyoEventTriggersSetup } from './KlaviyoEventTriggersSetup';
import { KlaviyoSetupMode } from './KlaviyoIntegrations';
import styles from './KlaviyoIntegrations.module.css';
import { KlaviyoOptInNotification } from './KlaviyoOptInNotification';
import { KlaviyoSuppressionSetup } from './KlaviyoSuppressionSetup';
import { KlaviyoSyncSetup } from './KlaviyoSyncSetup';
import { useConnectKlaviyo } from './use-connect-klaviyo';
import { KlaviyoRevenue } from './KlaviyoRevenue';

export const KlaviyoSettings = (props: {
  mode: KlaviyoSetupMode;
  onSelectMode: (mode: KlaviyoSetupMode) => void;
  showUpgradeModal: (onSuccessBilling?: () => void) => void;
  hasNativeIntegrationsFeature: boolean | undefined;
  klaviyoConnections: ConnectionModel[];
  connectToken: (tokenId?: number) => void;
  connectionsMessage: string | null;
}) => {
  const {
    klaviyoConnections,
    mode,
    onSelectMode,
    showUpgradeModal,
    connectToken,
    hasNativeIntegrationsFeature,
    connectionsMessage,
  } = props;
  const klaviyoConnection = klaviyoConnections[0];
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const helpLink =
    'https://customers.ai/help/l/en/article/n663a8hz34-klaviyo-integration';

  const { getLists, apiKeys, accounts, revoke, createApiKey, updateApiKey } =
    useConnectKlaviyo();
  const matchingApiKey = apiKeys.find(
    apiKey => apiKey.id === klaviyoConnection?.klaviyoAccessToken?.id,
  );
  const matchingOauthToken = accounts.find(
    token => token.id === klaviyoConnection?.klaviyoOauthToken?.id,
  );
  const matchingToken = matchingApiKey || matchingOauthToken;
  const tokenType = matchingApiKey ? 'api_key' : 'oauth_token';
  const notMyToken = matchingToken?.readOnly;

  return (
    <div className={styles.settingsContent}>
      <div className={classNames({ [styles.boxDisabled]: notMyToken })}>
        <KlaviyoRevenue />
        <KlaviyoOptInNotification />
        {mode === null && (
          <KlaviyoConnectionModeSelector
            helpLink={helpLink}
            onSelectMode={(mode: KlaviyoSetupMode) => onSelectMode(mode)}
          />
        )}
        {mode === 'oauth' && (
          <OauthTokenConnection
            helpLink={helpLink}
            controlsDisabled={!!notMyToken}
            token={matchingOauthToken}
            connectToken={connectToken}
            revoke={revoke}
            heading="Connect to Klaviyo Directly"
          />
        )}
        {mode === 'api_key' && (
          <KlaviyoApiKeyConnection
            hasNativeIntegrationsFeature={hasNativeIntegrationsFeature}
            helpLink={helpLink}
            controlsDisabled={!!notMyToken}
            token={matchingApiKey}
            createApiKey={createApiKey}
            updateApiKey={updateApiKey}
            showUpgradeModal={showUpgradeModal}
          />
        )}
      </div>
      {matchingToken && (
        <>
          <KlaviyoSyncSetup
            connectionsMessage={connectionsMessage}
            tokenType={tokenType}
            getLists={getLists}
            klaviyoConnections={klaviyoConnections}
            activeFbPageId={activeFbPageId}
            account={matchingToken}
            controlsDisabled={!!notMyToken}
          />
          <KlaviyoEventTriggersSetup controlsDisabled={!!notMyToken} />
          <KlaviyoSuppressionSetup
            controlsDisabled={!!notMyToken}
            tokenType={tokenType}
            tokenId={matchingToken.id}
            activeFbPageId={activeFbPageId}
          />
        </>
      )}
    </div>
  );
};
